import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp, Target, MessageSquare, Calendar, Users, Compass,
  Sprout, ArrowRight, Filter, HelpCircle, X, BookOpen, Archive
} from 'lucide-react';
import { ComingSoonBanner } from '../ComingSoonBanner';
import ReflectionModal from './components/ReflectionModal';

// Mock active development objectives (max 3)
const mockActiveObjectives = [
  {
    id: 'dev_obj_001',
    focus: 'Develop strategic thinking and product vision capabilities for senior leadership transition',
    linkedRole: { name: 'Senior Product Designer', effectiveDates: 'Jan 2024 - Present' },
    horizon: '6 months',
    supporter: { name: 'Michael Chen', role: 'Product Director' },
    evidenceSummary: 'Based on Q4 review insights highlighting strategic contributions and feedback patterns showing cross-team influence',
    lastDiscussed: '2024-12-15',
    createdDate: '2024-11-01',
    reflections: [
      { id: 'refl_001', date: '2024-12-10', note: 'Led product roadmap discussion with stakeholders. Practiced articulating long-term vision and connecting features to business outcomes.' },
      { id: 'refl_002', date: '2024-11-25', note: 'Attended strategic planning workshop. Learning to balance user needs with business constraints.' },
    ],
    coachingReferences: [
      { date: '2024-12-15', type: 'check-in', summary: 'Discussed progress on stakeholder presentations' },
      { date: '2024-11-20', type: 'coaching', summary: 'Practiced executive communication techniques' },
    ],
  },
  {
    id: 'dev_obj_002',
    focus: 'Build cross-functional collaboration skills and influence without authority',
    linkedRole: { name: 'Senior Product Designer', effectiveDates: 'Jan 2024 - Present' },
    horizon: '3 months',
    supporter: { name: 'Michael Chen', role: 'Product Director' },
    evidenceSummary: 'Multiple feedback mentions of effective collaboration with engineering and design teams during Q3 projects',
    lastDiscussed: '2024-12-01',
    createdDate: '2024-10-15',
    reflections: [
      { id: 'refl_003', date: '2024-12-05', note: 'Successfully facilitated design-engineering sync. Used active listening and found common ground on technical constraints vs. user experience goals.' },
    ],
    coachingReferences: [
      { date: '2024-12-01', type: 'check-in', summary: 'Reviewed collaboration wins from recent sprint' },
    ],
  },
  {
    id: 'dev_obj_003',
    focus: 'Enhance data-driven decision making and analytics interpretation skills',
    linkedRole: { name: 'Senior Product Designer', effectiveDates: 'Jan 2024 - Present' },
    horizon: '6 months',
    supporter: { name: 'Emily Rodriguez', role: 'Analytics Lead' },
    evidenceSummary: 'Review synthesis identified opportunity to strengthen quantitative analysis skills for design validation',
    lastDiscussed: '2024-11-28',
    createdDate: '2024-10-01',
    reflections: [],
    coachingReferences: [
      { date: '2024-11-28', type: 'coaching', summary: 'Introduction to analytics tools and metrics frameworks' },
    ],
  },
];

const mockArchivedObjectives = [
  {
    id: 'dev_obj_archived_001',
    focus: 'Master advanced prototyping tools and interactive design techniques',
    linkedRole: { name: 'Product Designer', effectiveDates: 'Jan 2023 - Dec 2023' },
    completedDate: '2024-06-30',
    outcome: 'Successfully completed. Now leading prototyping workshops for the design team.',
    keyLearnings: 'Consistent practice and teaching others accelerated mastery.',
    lifecycle: [
      { date: '2024-01-15', event: 'Objective created', type: 'created' },
      { date: '2024-05-15', event: 'Led first prototyping workshop', type: 'milestone' },
      { date: '2024-06-30', event: 'Objective completed', type: 'completed' },
    ],
  },
  {
    id: 'dev_obj_archived_002',
    focus: 'Develop user research and interview facilitation capabilities',
    linkedRole: { name: 'Product Designer', effectiveDates: 'Jan 2023 - Dec 2023' },
    completedDate: '2024-03-31',
    outcome: 'Completed successfully. Conducted 15+ user interviews and presented findings to product team.',
    keyLearnings: 'Building rapport with users is as important as asking the right questions.',
    lifecycle: [
      { date: '2024-01-05', event: 'Objective created', type: 'created' },
      { date: '2024-02-15', event: 'Conducted first solo interview', type: 'milestone' },
      { date: '2024-03-31', event: 'Objective completed', type: 'completed' },
    ],
  },
];

export default function EmployeeDevelopmentHub() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>(() => {
    return localStorage.getItem('devHubActiveTab') || 'active';
  });
  const [activeFilter, setActiveFilter] = useState('all');
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [selectedObjective, setSelectedObjective] = useState<typeof mockActiveObjectives[0] | null>(null);
  const [isReflectionModalOpen, setIsReflectionModalOpen] = useState(false);

  const handleAddReflection = (objective: typeof mockActiveObjectives[0]) => {
    setSelectedObjective(objective);
    setIsReflectionModalOpen(true);
  };

  const handleCloseReflectionModal = () => {
    setIsReflectionModalOpen(false);
    setSelectedObjective(null);
  };

  const handleSaveReflection = (reflection: { id: string; date: string; note: string }) => {
    console.log('Saved reflection:', reflection);
    handleCloseReflectionModal();
  };

  useEffect(() => {
    localStorage.setItem('devHubActiveTab', activeTab);
  }, [activeTab]);

  const activeObjectives = mockActiveObjectives;
  const archivedObjectives = mockArchivedObjectives;

  // Calculate development snapshot metrics
  const getLast90DaysReflections = () => {
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    let count = 0;
    activeObjectives.forEach(obj => {
      obj.reflections.forEach(refl => {
        if (new Date(refl.date) >= ninetyDaysAgo) count++;
      });
    });
    return count;
  };

  const getLastConversationDate = () => {
    const dates = activeObjectives.map(obj => obj.lastDiscussed).filter(Boolean);
    if (dates.length === 0) return null;
    return dates.sort().reverse()[0];
  };

  const getUniqueSupporters = () => {
    const supporters = new Map<string, { name: string; role: string }>();
    activeObjectives.forEach(obj => {
      if (obj.supporter) {
        supporters.set(obj.supporter.name, obj.supporter);
      }
    });
    return Array.from(supporters.values());
  };

  // Filter objectives
  const getFilteredObjectives = () => {
    if (activeFilter === 'all') return activeObjectives;
    const now = new Date();
    return activeObjectives.filter(obj => {
      if (activeFilter === 'recent') {
        const lastDiscussed = obj.lastDiscussed ? new Date(obj.lastDiscussed) : null;
        if (!lastDiscussed) return false;
        const daysSince = Math.floor((now.getTime() - lastDiscussed.getTime()) / (1000 * 60 * 60 * 24));
        return daysSince <= 14;
      }
      if (activeFilter === 'needs-reflection') {
        return !obj.reflections || obj.reflections.length === 0;
      }
      return true;
    });
  };

  const filteredObjectives = getFilteredObjectives();

  const tabs = [
    { id: 'active', label: 'Active Development', icon: Target, count: activeObjectives.length },
    { id: 'archived', label: 'Development History', icon: Archive, count: archivedObjectives.length },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <ComingSoonBanner
        icon={<Target className="w-6 h-6" />}
        title="Employee Development Hub"
        description="Employee Development Hub is currently in development. This is a preview of the upcoming feature."
        gradientFrom="from-emerald-500"
        gradientTo="to-green-600"
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">{t('Development')}</h1>
              <p className="text-muted-foreground text-base mb-1">
                {t('What I am actively developing in my role')}
              </p>
              <p className="text-xs text-muted-foreground italic">
                {t('Development evolves through reflection, coaching, and applied experiences.')}
              </p>
              <button
                
                className="text-xs text-primary mt-1 flex items-center space-x-1"
               
              >
                <HelpCircle className="w-3 h-3" />
                <span>{t('How this works')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Personal Development Snapshot */}
      <div className="mb-8 bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6">
        <h2 className="text-sm font-semibold text-foreground mb-4 flex items-center space-x-2">
          <Compass className="w-4 h-4 text-purple-600" />
          <span>{t('Personal Development Snapshot')}</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">{t('Active Objectives')}</span>
              <Target className="w-3.5 h-3.5 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-foreground">{activeObjectives.length} / 3</p>
          </div>
          <div className="bg-white/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">{t('Reflections (90 days)')}</span>
              <MessageSquare className="w-3.5 h-3.5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-foreground">{getLast90DaysReflections()}</p>
          </div>
          <div className="bg-white/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">{t('Last Conversation')}</span>
              <Calendar className="w-3.5 h-3.5 text-green-600" />
            </div>
            <p className="text-sm font-semibold text-foreground">
              {getLastConversationDate()
                ? new Date(getLastConversationDate()!).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                : t('Not yet')}
            </p>
          </div>
          <div className="bg-white/50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground">{t('Primary Supporters')}</span>
              <Users className="w-3.5 h-3.5 text-orange-600" />
            </div>
            <div className="flex -space-x-2">
              {getUniqueSupporters().slice(0, 3).map((supporter, idx) => (
                <div
                  key={idx}
                  className="w-8 h-8 rounded-full border-2 border-white bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-xs font-medium"
                  title={supporter.name}
                >
                  {supporter.name.split(' ').map(n => n[0]).join('')}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-border mb-6">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{t(tab.label)}</span>
              <span className={`inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full ${
                activeTab === tab.id ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* Active Development Tab */}
      {activeTab === 'active' && (
        <div>
          {/* Filter bar */}
          <div className="flex items-center space-x-2 mb-6">
            <Filter className="w-4 h-4 text-muted-foreground" />
            {['all', 'recent', 'needs-reflection'].map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  activeFilter === filter
                    ? 'bg-primary text-white'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {t(filter === 'all' ? 'All' : filter === 'recent' ? 'Recently Active' : 'Needs Reflection')}
              </button>
            ))}
          </div>

          {filteredObjectives.length === 0 && activeFilter === 'all' ? (
            <div className="bg-card border border-border rounded-xl p-12 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sprout className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">{t('No Active Development Objectives')}</h3>
              <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                {t('Development objectives emerge from conversations and insight--not urgency.')}
              </p>
              <div className="mt-6 flex items-center justify-center space-x-8 text-sm text-muted-foreground">
                <div className="flex flex-col items-center">
                  <MessageSquare className="w-6 h-6 text-purple-400 mb-2" />
                  <span>{t('Reflection')}</span>
                </div>
                <ArrowRight className="w-5 h-5 text-border" />
                <div className="flex flex-col items-center">
                  <Users className="w-6 h-6 text-blue-400 mb-2" />
                  <span>{t('Conversation')}</span>
                </div>
                <ArrowRight className="w-5 h-5 text-border" />
                <div className="flex flex-col items-center">
                  <Target className="w-6 h-6 text-green-400 mb-2" />
                  <span>{t('Objective')}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {filteredObjectives.map((objective) => (
                <div key={objective.id} className="bg-card border border-border rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-foreground mb-2">{objective.focus}</h3>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <span>{objective.linkedRole.name}</span>
                        <span>|</span>
                        <span>{t('Horizon')}: {objective.horizon}</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground mb-4">{objective.evidenceSummary}</p>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span className="flex items-center space-x-1">
                        <MessageSquare className="w-3 h-3" />
                        <span>{objective.reflections.length} {t('reflections')}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3" />
                        <span>{t('Last discussed')}: {objective.lastDiscussed ? new Date(objective.lastDiscussed).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : t('Never')}</span>
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleAddReflection(objective)}
                        className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                      >
                        {t('Add Reflection')}
                      </button>
                      <button
                        className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                      >
                        {t('View Details')}
                      </button>
                    </div>
                  </div>

                  {/* Supporter */}
                  <div className="mt-4 pt-4 border-t border-border flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-xs font-medium">
                      {objective.supporter.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{objective.supporter.name}</p>
                      <p className="text-xs text-muted-foreground">{objective.supporter.role}</p>
                    </div>
                  </div>
                </div>
              ))}

              {filteredObjectives.length === 0 && activeFilter !== 'all' && (
                <div className="bg-card border border-border rounded-xl p-8 text-center">
                  <Filter className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">{t('No objectives match this filter')}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Archived Development Tab */}
      {activeTab === 'archived' && (
        <div className="space-y-6">
          {archivedObjectives.map((objective) => (
            <div key={objective.id} className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Archive className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-foreground">{objective.focus}</h3>
                  <p className="text-sm text-muted-foreground">
                    {objective.linkedRole.name} | {t('Completed')}: {new Date(objective.completedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">{t('Outcome')}</p>
                  <p className="text-sm text-foreground">{objective.outcome}</p>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">{t('Key Learnings')}</p>
                  <p className="text-sm text-foreground">{objective.keyLearnings}</p>
                </div>
                {/* Timeline */}
                <div className="mt-4">
                  <p className="text-xs font-medium text-muted-foreground mb-2">{t('Lifecycle')}</p>
                  <div className="space-y-2">
                    {objective.lifecycle.map((item, idx) => (
                      <div key={idx} className="flex items-center space-x-3 text-sm">
                        <div className={`w-2 h-2 rounded-full ${
                          item.type === 'completed' ? 'bg-green-500' : item.type === 'milestone' ? 'bg-blue-500' : 'bg-gray-400'
                        }`} />
                        <span className="text-muted-foreground text-xs">{new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        <span className="text-foreground">{item.event}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* How It Works Modal */}
      {showHowItWorks && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="text-xl font-bold text-foreground">{t('How Development Works')}</h2>
              <button
                onClick={() => setShowHowItWorks(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{t('Development Objectives')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('Created collaboratively with your manager based on review insights, feedback patterns, and career aspirations.')}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{t('Reflections')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('Document what you tried, learned, or noticed. Reflections help track growth and identify patterns over time.')}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{t('Coaching Conversations')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('Regular check-ins with your supporter to discuss progress, challenges, and next steps.')}
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{t('Continuous Growth')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('Development is about who you are becoming at work, not just what you are achieving.')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reflection Modal */}
      {selectedObjective && (
        <ReflectionModal
          objective={selectedObjective}
          onClose={handleCloseReflectionModal}
          onSave={handleSaveReflection}
        />
      )}
    </div>
  );
}
